import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, Navigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import { ComparisonProvider } from './context/ComparisonContext';
import { WishlistProvider } from './context/WishlistContext';
import { ResponsiveProvider } from './context/ResponsiveContext';
import { LanguageProvider } from './context/LanguageContext';
import { PaymentProvider, usePayment } from './context/PaymentContext';
import PaymentStatusChecker from './components/PaymentStatusChecker';
import ScrollToTop from './components/ScrollToTop';
import RouteTranslationHandler from './components/RouteTranslationHandler';
import TranslationLoadingOverlay from './components/TranslationLoadingOverlay';
import './App.css';
import { migrateStoredBranding } from './utils/brandText';

// Lazy load all components for code splitting and faster initial load
const SupabaseExample = lazy(() => import('./components/SupabaseExample'));
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const CustomerDashboard = lazy(() => import('./components/CustomerDashboard'));
const CustomerOrderTracking = lazy(() => import('./components/CustomerOrderTracking'));
const AddBanker = lazy(() => import('./components/AddBanker'));
const BankersList = lazy(() => import('./components/BankersList'));
const ProductionHome = lazy(() => import('./components/ProductionHome'));
const ProductDetail = lazy(() => import('./components/ProductDetail'));
const ProductsPage = lazy(() => import('./components/ProductsPage'));
const AboutUs = lazy(() => import('./components/AboutUs'));
const ContactUs = lazy(() => import('./components/ContactUs'));
const Certificates = lazy(() => import('./components/Certificates'));
const CartPage = lazy(() => import('./components/CartPage'));
const CheckoutPage = lazy(() => import('./components/CheckoutPage'));
const ThankYouPage = lazy(() => import('./components/ThankYouPage'));
const PaymentSuccess = lazy(() => import('./components/PaymentSuccess'));
const PaymentFailure = lazy(() => import('./components/PaymentFailure'));
const FaysalPayCheckout = lazy(() => import('./components/FaysalPayCheckout'));
const MyAccount = lazy(() => import('./components/MyAccount'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const HimalayanPinkSalt = lazy(() => import('./components/HimalayanPinkSalt'));
const OnyxMarbleGranite = lazy(() => import('./components/OnyxMarbleGranite'));
const PreciousStones = lazy(() => import('./components/PreciousStones'));
const Apparel = lazy(() => import('./components/Apparel'));
const ShippingOptionsPolicy = lazy(() => import('./components/ShippingOptionsPolicy'));
const ShippingCostsPolicy = lazy(() => import('./components/ShippingCostsPolicy'));
const DeliveryTimesPolicy = lazy(() => import('./components/DeliveryTimesPolicy'));
const ShippingVendorDashboard = lazy(() => import('./components/ShippingVendorDashboard'));
const ShippingVendorProfile = lazy(() => import('./components/ShippingVendorProfile'));
const MyBids = lazy(() => import('./components/MyBids'));
const ShippersBid = lazy(() => import('./components/ShippersBid'));
const ShippingVendorMyOrders = lazy(() => import('./components/ShippingVendorMyOrders'));
const ShippingVendorCustomInvoice = lazy(() => import('./components/ShippingVendorCustomInvoice'));
const MarketersPortal = lazy(() => import('./components/MarketersPortal'));
const AccountsPortal = lazy(() => import('./components/AccountsPortal'));
const BankersPortal = lazy(() => import('./components/BankersPortal'));
const Sales = lazy(() => import('./components/Sales'));
const ReturnPolicy = lazy(() => import('./components/ReturnPolicy'));
const RefundPolicy = lazy(() => import('./components/RefundPolicy'));
const PaymentTermsPolicy = lazy(() => import('./components/PaymentTermsPolicy'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const Inspiration = lazy(() => import('./components/Inspiration'));
const CreateTradeAccount = lazy(() => import('./components/CreateTradeAccount'));
const SignIn = lazy(() => import('./components/SignIn'));
const TraderLogin = lazy(() => import('./components/TraderLogin'));
const ComparisonPage = lazy(() => import('./components/ComparisonPage'));
const WishlistPage = lazy(() => import('./components/WishlistPage'));
const Profile = lazy(() => import('./components/Profile'));
const CustomerAgentLogin = lazy(() => import('./components/CustomerAgentLogin'));
const CustomerAgentDashboard = lazy(() => import('./components/CustomerAgentDashboard'));

// Loading component
const LoadingSpinner = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '50vh',
    flexDirection: 'column'
  }}>
    <div className="loading-spinner" style={{
      width: '40px',
      height: '40px',
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #3498db',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
    <p style={{ marginTop: '10px', color: '#666' }}>Loading...</p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

function App() {
  const [connectionStatus, setConnectionStatus] = useState('Testing connection...');
  const [isConnected, setIsConnected] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    migrateStoredBranding();
    testConnection();
  }, []);

  const testConnection = async () => {
    try {
      // Test connection by querying a table that should exist
      const { data, error } = await supabase.from('orders').select('id').limit(1);
      
      if (error && error.code === 'PGRST116') {
        // This error means the table doesn't exist, but the connection is working
        setConnectionStatus('Connected to Supabase successfully!');
        setIsConnected(true);
      } else if (error) {
        setConnectionStatus(`Connection error: ${error.message}`);
        setIsConnected(false);
      } else {
        setConnectionStatus('Connected to Supabase successfully!');
        setIsConnected(true);
      }
    } catch (error) {
      setConnectionStatus(`Connection failed: ${error.message}`);
      setIsConnected(false);
    }
  };

  // Create a wrapper component for the login form to use useNavigate
  const LoginForm = () => {
    return (
      <div></div>
    );
  };

  return (
    <LanguageProvider>
      <AuthProvider>
        <ComparisonProvider>
          <WishlistProvider>
            <ResponsiveProvider>
            <PaymentProvider>
            <Router>
          <div className="App">
            <ScrollToTop />
            <RouteTranslationHandler />
            <TranslationLoadingOverlay />
            <Suspense fallback={<LoadingSpinner />}>
            <PaymentStatusChecker />
            <Routes>
            {/* Admin Login */}
            <Route path="/admin" element={<AdminLogin />} />
            {/* Admin Dashboard Routes - Each page has its own URL */}
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/:page" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route path="/" element={<CustomerDashboard />} />
            
            {/* Portal Routes */}
            <Route path="/sales" element={<Sales />} />
            
            <Route path="/customers" element={<CustomerDashboard />} />
            <Route path="/track-orders" element={<CustomerOrderTracking />} />
            
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/certificates" element={<Certificates />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/checkout/payment" element={<FaysalPayCheckout />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/failure" element={<PaymentFailure />} />
            <Route path="/my-account" element={<MyAccount />} />
            <Route path="/profile/:customerId" element={<Profile />} />
            <Route path="/compare" element={<ComparisonPage />} />
            <Route path="/wishlist" element={<WishlistPage />} />
            <Route path="/category/himalayan-pink-salt" element={<HimalayanPinkSalt />} />
            <Route path="/category/onyx-marble-granite" element={<OnyxMarbleGranite />} />
            <Route path="/category/precious-stones" element={<PreciousStones />} />
                    <Route path="/category/apparel" element={<Apparel />} />
        <Route path="/inspiration" element={<Inspiration />} />
        <Route path="/create-trade-account" element={<CreateTradeAccount />} />
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/trader-login" element={<TraderLogin />} />

        {/* Policy Pages Routes */}
            <Route path="/shipping-options-policy" element={<ShippingOptionsPolicy />} />
            <Route path="/shipping-costs-policy" element={<ShippingCostsPolicy />} />
            <Route path="/delivery-times-policy" element={<DeliveryTimesPolicy />} />
            <Route path="/return-policy" element={<ReturnPolicy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/payment-terms-policy" element={<PaymentTermsPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            
            <Route path="/bankers" element={<BankersPortal />} />
            <Route path="/bankers/dashboard" element={<BankersPortal />} />
            <Route path="/bankers/payment-slips" element={<BankersPortal />} />
            <Route path="/bankers/production-progress" element={<BankersPortal />} />
            <Route path="/bankers/profile" element={<BankersPortal />} />
            <Route path="/bankers/add" element={<AddBanker />} />
            <Route path="/bankers/list" element={<BankersList />} />
            
            <Route path="/production" element={<ProductionHome />} />
            
            <Route path="/shipping-vendors" element={<ShippingVendorDashboard />} />
            <Route path="/shipping-vendors/profile" element={<ShippingVendorProfile />} />
            <Route path="/shipping-vendors/my-bids" element={<MyBids />} />
            <Route path="/shipping-vendors/my-orders" element={<ShippingVendorMyOrders />} />
            <Route path="/shipping-vendors/custom-invoice" element={<ShippingVendorCustomInvoice />} />
            <Route path="/shipping-vendors/shippers-bid" element={<ShippersBid />} />
            
            <Route path="/marketers" element={<MarketersPortal />} />
            <Route path="/accounts-portal" element={<AccountsPortal />} />
            <Route path="/accountant-portal" element={<AccountsPortal />} />
            
            {/* Customer Agent Routes */}
            <Route path="/customersupport" element={<CustomerAgentLogin />} />
            <Route path="/customersupport/dashboard" element={<CustomerAgentDashboard />} />
          </Routes>
          </Suspense>
        </div>
          </Router>
            </PaymentProvider>
            </ResponsiveProvider>
          </WishlistProvider>
        </ComparisonProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
